import type { FC, ReactNode } from "react";
import { mergeClassNames, alignmentToClassName } from "./utility";

interface BFLTableColumn {
  key: string;
  title: string;
  widthClassName?: string;
  headerClassName?: string;
  cellClassName?: string;
  align?: "left" | "center" | "right";
  isRowHeader?: boolean;
}

interface BFLTableRow {
  key?: string;
  cells: Record<string, ReactNode>;
}

interface BFLTableProps {
  columns?: BFLTableColumn[];
  rows?: BFLTableRow[];
  caption?: string;
  className?: string;
  headerRowClassName?: string;
  tableWrapperClassName?: string;
  bodyClassName?: string;
  emptyMessage?: string;
  emptyStateClassName?: string;
  bodyRowClassName?: string;
  tableHeaderClassName?: string;
}

const BFLTable: FC<BFLTableProps> = ({
  columns = [],
  rows = [],
  caption,
  className,
  headerRowClassName,
  bodyRowClassName,
  tableWrapperClassName,
  tableHeaderClassName,
  bodyClassName,
  emptyMessage = "No data available.",
  emptyStateClassName,
}) => {
  if (!columns?.length) {
    return null;
  }

  if (!rows?.length) {
    return (
      <div
        className={mergeClassNames(
          "w-full rounded-[4px] border border-gray-200 bg-white p-6 text-center text-sm text-gray-500",
          className,
          emptyStateClassName,
        )}
      >
        {emptyMessage}
      </div>
    );
  }

  return (

    <div className={mergeClassNames("max-h-[320px] max-sm:max-h-[290px] overflow-y-auto border-y-[1px] border-l-[1px] border-[#E5E5E5] rounded-[4px] overscroll-none", tableWrapperClassName)}>
      <table
        className={mergeClassNames(
          "w-full text-nowrap border-collapse ",
          className,
        )}
      >
        {caption ? <caption className="sr-only">{caption}</caption> : null}
        <thead className={mergeClassNames("bg-[#F2F2F2]", tableHeaderClassName)}>
          <tr className={headerRowClassName}>
            {columns.map((column) => (
              <th
                key={column.key}
                scope="col"
                className={mergeClassNames(
                  "p-[12px] font-medium border-r-[1px] border-[#E5E5E5] sticky -top-[2px] bg-[#F2F2F2] z-20",
                  alignmentToClassName(column.align),
                  column.headerClassName,
                )}
              >
                <span className="max-sm:text-[12px] text-[14px] text-[#1A1A1A]">
                  {column.title}
                </span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody className={bodyClassName}>
          {rows.map((row, rowIndex) => {
            const backgroundClass = rowIndex % 2 === 0 ? 'bg-white' : 'bg-[#FAFAFA]'
            const trKey = row.key ?? `row-${rowIndex}`;
            return (
              <tr key={trKey} className={mergeClassNames(
                backgroundClass,
                bodyRowClassName
              )}>
                {columns.map((column, columnIndex) => {
                  const cellContent = row.cells?.[column.key];
                  const isRowHeader = column.isRowHeader ?? columnIndex === 0;
                  const alignmentClass = alignmentToClassName(column.align);
                  const textClassName = mergeClassNames(
                    "text-[14px] text-[#1A1A1A]",
                    isRowHeader && "font-medium",
                  );
                  const cellKey = row.key ? `${row.key}-${column.key}` : `row-${rowIndex}-${column.key}`;

                  return (
                    <td
                      key={cellKey}
                      className={mergeClassNames(
                        "border-t-[1px] border-r-[1px] border-[#E5E5E5] p-[12px]",
                        alignmentClass,
                        column.cellClassName,
                      )}
                    >
                      <span className={textClassName}>{cellContent ?? "-"}</span>
                    </td>
                  );
                })}
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  );
};

export default BFLTable;

export type { BFLTableColumn, BFLTableRow, BFLTableProps };

